import { createErrorHandlingCaches } from '@hprofits/api-client/error-handling';
import { RequestPayloadValidationError, ResponsePayloadValidationError } from '@hprofits/api-client/errors';
import { runDetached } from '@hprofits/utils';
import { MutationCache, QueryCache, QueryClient, VueQueryPlugin } from '@tanstack/vue-query';
import type { App } from 'vue';
import { z } from 'zod';

import { getAuthSession } from '@/auth/session';
import router from '@/router';
import { writeErrorToSentry } from '@/sentry';
import { readActiveRole } from '@/utils/auth';
import { saveReloginContext } from '@/utils/reloginContext';

import { showConnectionErrorToast, showErrorToast } from './errorToasts';

export { setErrorToastHandler, showErrorToast } from './errorToasts';

// eslint-disable-next-line @typescript-eslint/no-magic-numbers
const STALE_TIME_MS = 1000 * 60; // 1 minute

const { queryCache, mutationCache } = createErrorHandlingCaches({
  displayError: showErrorToast,
  handlingStrategy: {
    unexpected: (error) => {
      if (error instanceof RequestPayloadValidationError || error instanceof ResponsePayloadValidationError) {
        // eslint-disable-next-line no-console
        console.error('[api-client] Payload validation error\n%s', z.prettifyError(error.validationError), {
          issues: error.validationError.issues,
          error,
        });
      } else {
        // eslint-disable-next-line no-console
        console.error('[api-client] Unexpected error:', error);
      }
      writeErrorToSentry(error);
    },
    unauthorized: () => {
      // eslint-disable-next-line no-console
      console.error('[api-client] Unauthorized error');
      if (getAuthSession().isAccessTokenExpiring(0)) {
        return;
      }
      getAuthSession().logout();
      saveReloginContext({
        url: router.currentRoute.value?.fullPath,
        role: readActiveRole(),
      });
      runDetached(router.push({ name: 'login' }));
    },
    network: (displayError) => {
      // eslint-disable-next-line no-console
      console.error('[api-client] Network error');
      showConnectionErrorToast(displayError);
    },
  },
});

export const queryClient = new QueryClient({
  queryCache: new QueryCache(queryCache),
  mutationCache: new MutationCache(mutationCache),
  defaultOptions: {
    queries: {
      staleTime: STALE_TIME_MS,
      retry: 1,
    },
  },
});

/**
 * Vue plugin installer
 */
export const installVueQuery = (app: App): void => {
  app.use(VueQueryPlugin, { queryClient });
};

export default installVueQuery;
